import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { FaTrash, FaPencil } from "react-icons/fa6";
import type { ColDef, RowClickedEvent } from "ag-grid-community";
import BaseTemplate from "../components/BaseTemplate";
import PersistentGrid from "../components/PersistentGrid/PersistentGrid";
import PersistentGridResetButton from "../components/PersistentGrid/PersistentGridResetButton";
import PersistentGridClearFiltersButton from "../components/PersistentGrid/PersistentGridClearFiltersButton";
import { PersistentGridProvider } from "../contexts/PersistentGridContext";
import { useApi } from "../hooks/useApi";
import { NotificationContext } from "../components/NotificationPane";
import type { FppCondition, FppProcess, FppRuleGroup, NotificationContextType } from "../types";
import InternalUserRoute from "../util/internal_user_route";
import { buildDefaultColDef, EDB_CUSTOM_withHeaderTooltips } from "../util/AgGridFunctions";
import { useColumnDefaults } from "../hooks/useColumnDefaults";

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const FIELD_OPTIONS = [
  "filename", "file_type", "source",
  "email_from", "email_subject", "sftp_user",
  "key_id", "doc_family", "doc_from",
];

const OPERATOR_OPTIONS = [
  { value: "eq",          label: "Equals" },
  { value: "not_eq",      label: "Not Equals" },
  { value: "contains",    label: "Contains" },
  { value: "not_contains",label: "Not Contains" },
  { value: "regex",       label: "Regex" },
  { value: "not_regex",   label: "Not Regex" },
];

const EMPTY_CONDITION_FORM = {
  field_name: FIELD_OPTIONS[0],
  operator: "eq",
  match_value: "",
  case_sensitive: false,
};

// -----------------------------------------------------------------------------
// ProcessMapping page
// -----------------------------------------------------------------------------

function ProcessMapping() {
  const {
    getProcesses, postProcess, putProcess, deleteProcess,
    getRuleGroups, postRuleGroup, putRuleGroup, deleteRuleGroup,
    getConditions, postCondition, putCondition, deleteCondition,
  } = useApi();
  const columnDefaults = useColumnDefaults();

  const { pushNotification } = useContext<NotificationContextType | null>(NotificationContext)!;

  const [processes, setProcesses]     = useState<FppProcess[]>([]);
  const [ruleGroups, setRuleGroups]   = useState<FppRuleGroup[]>([]);
  const [conditions, setConditions]   = useState<FppCondition[]>([]);
  const [loading, setLoading]         = useState(true);
  const [savingRule, setSavingRule]   = useState(false);

  // Modals
  const { isOpen: isRuleOpen,    onOpen: onRuleOpen,    onClose: onRuleClose }    = useDisclosure();
  const { isOpen: isProcessOpen, onOpen: onProcessOpen, onClose: onProcessClose } = useDisclosure();

  // Rule group form
  const [selectedGroup, setSelectedGroup]   = useState<FppRuleGroup | null>(null);
  const [ruleForm, setRuleForm]             = useState({ process_id: 0, description: "" });
  const [conditionForm, setConditionForm]   = useState(EMPTY_CONDITION_FORM);
  const [editingCondition, setEditingCondition] = useState<FppCondition | null>(null);

  // Process form
  const [selectedProcess, setSelectedProcess]   = useState<FppProcess | null>(null);
  const [processForm, setProcessForm]           = useState({ process_name: "", description: "" });

  // -----------------------------------------------------------------------
  // Data loading
  // -----------------------------------------------------------------------

  const loadRuleGroups = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getRuleGroups();
      setRuleGroups(data);
    } catch {
      pushNotification("Failed to load mapping rules.", "error");
    } finally {
      setLoading(false);
    }
  }, [getRuleGroups, pushNotification]);

  const loadProcesses = useCallback(async () => {
    try {
      const data = await getProcesses();
      setProcesses(data);
    } catch {
      pushNotification("Failed to load processes.", "error");
    }
  }, [getProcesses, pushNotification]);

  const loadConditions = useCallback(async (ruleGroupId: number) => {
    try {
      const data = await getConditions(ruleGroupId);
      setConditions(data);
    } catch {
      pushNotification("Failed to load conditions.", "error");
    }
  }, [getConditions, pushNotification]);

  useEffect(() => {
    loadRuleGroups();
    loadProcesses();
  }, [loadRuleGroups, loadProcesses]);

  // -----------------------------------------------------------------------
  // Rule group grid
  // -----------------------------------------------------------------------

  const columnDefs = useMemo<ColDef<FppRuleGroup>[]>(() => EDB_CUSTOM_withHeaderTooltips([
    { field: "process_name",    headerName: "Process",     ...buildDefaultColDef(columnDefaults, "process_name"),    minWidth: 180 },
    { field: "description",     headerName: "Rule",        ...buildDefaultColDef(columnDefaults, "description"),     minWidth: 220, flex: 1 },
    { field: "condition_count", headerName: "Conditions",  ...buildDefaultColDef(columnDefaults, "condition_count"), minWidth: 120 },
  ]), [columnDefaults]);

  const defaultColDef = useMemo<ColDef<FppRuleGroup>>(() => ({
    ...buildDefaultColDef(columnDefaults),
    sortable: true, resizable: true, filter: true, floatingFilter: true,
  }), [columnDefaults]);

  const handleRowClicked = async (event: RowClickedEvent<FppRuleGroup>) => {
    if (!event.data) return;
    const group = event.data;
    setSelectedGroup(group);
    setRuleForm({ process_id: group.process_id, description: group.description });
    setConditionForm(EMPTY_CONDITION_FORM);
    setEditingCondition(null);
    await loadConditions(group.rule_group_id);
    onRuleOpen();
  };

  // -----------------------------------------------------------------------
  // Rule group modal — save / delete
  // -----------------------------------------------------------------------

  const handleRuleSave = async () => {
    if (!ruleForm.process_id) {
      pushNotification("Please select a process.", "warning");
      return;
    }
    try {
      setSavingRule(true);
      if (selectedGroup) {
        await putRuleGroup({ rule_group_id: selectedGroup.rule_group_id, ...ruleForm });
        pushNotification("Rule updated.", "success");
      } else {
        await postRuleGroup(ruleForm);
        pushNotification("Rule added.", "success");
      }
      await loadRuleGroups();
      handleRuleClose();
    } catch {
      pushNotification("Failed to save rule.", "error");
    } finally {
      setSavingRule(false);
    }
  };

  const handleRuleDelete = async () => {
    if (!selectedGroup) return;
    try {
      await deleteRuleGroup(selectedGroup.rule_group_id);
      pushNotification("Rule deleted.", "success");
      await loadRuleGroups();
      handleRuleClose();
    } catch {
      pushNotification("Failed to delete rule.", "error");
    }
  };

  const handleRuleClose = () => {
    setSelectedGroup(null);
    setConditions([]);
    setConditionForm(EMPTY_CONDITION_FORM);
    setEditingCondition(null);
    onRuleClose();
  };

  const openAddRule = () => {
    setSelectedGroup(null);
    setConditions([]);
    setRuleForm({ process_id: processes[0]?.process_id ?? 0, description: "" });
    setConditionForm(EMPTY_CONDITION_FORM);
    setEditingCondition(null);
    onRuleOpen();
  };

  // -----------------------------------------------------------------------
  // Conditions — add / edit / delete
  // -----------------------------------------------------------------------

  const handleAddCondition = async () => {
    if (!conditionForm.match_value.trim()) {
      pushNotification("Please enter a match value.", "warning");
      return;
    }
    const ruleGroupId = selectedGroup?.rule_group_id;
    if (!ruleGroupId) {
      pushNotification("Save the rule first before adding conditions.", "warning");
      return;
    }
    try {
      await postCondition({ rule_group_id: ruleGroupId, ...conditionForm });
      setConditionForm(EMPTY_CONDITION_FORM);
      await loadConditions(ruleGroupId);
      await loadRuleGroups();
    } catch {
      pushNotification("Failed to add condition.", "error");
    }
  };

  const handleEditConditionSave = async () => {
    if (!editingCondition) return;
    try {
      await putCondition({
        condition_id:   editingCondition.condition_id,
        field_name:     editingCondition.field_name,
        operator:       editingCondition.operator,
        match_value:    editingCondition.match_value,
        case_sensitive: editingCondition.case_sensitive,
      });
      setEditingCondition(null);
      await loadConditions(editingCondition.rule_group_id);
    } catch {
      pushNotification("Failed to update condition.", "error");
    }
  };

  const handleDeleteCondition = async (conditionId: number, ruleGroupId: number) => {
    try {
      await deleteCondition(conditionId);
      await loadConditions(ruleGroupId);
      await loadRuleGroups();
    } catch {
      pushNotification("Failed to delete condition.", "error");
    }
  };

  // -----------------------------------------------------------------------
  // Process modal — save / delete
  // -----------------------------------------------------------------------

  const openProcessModal = () => {
    setSelectedProcess(null);
    setProcessForm({ process_name: "", description: "" });
    onProcessOpen();
  };

  const handleProcessSave = async () => {
    if (!processForm.process_name.trim()) {
      pushNotification("Please enter a process name.", "warning");
      return;
    }
    try {
      if (selectedProcess) {
        await putProcess({ ...selectedProcess, ...processForm });
        pushNotification("Process updated.", "success");
      } else {
        await postProcess(processForm);
        pushNotification("Process added.", "success");
      }
      await loadProcesses();
      setSelectedProcess(null);
      setProcessForm({ process_name: "", description: "" });
    } catch {
      pushNotification("Failed to save process.", "error");
    }
  };

  const handleProcessDelete = async (process: FppProcess) => {
    try {
      await deleteProcess(process.process_id);
      pushNotification("Process deleted.", "success");
      await loadProcesses();
    } catch {
      pushNotification("Failed to delete process.", "error");
    }
  };

  const selectProcessForEdit = (process: FppProcess) => {
    setSelectedProcess(process);
    setProcessForm({ process_name: process.process_name, description: process.description ?? "" });
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <InternalUserRoute>
      <PersistentGridProvider gridKey="process-mapping-rule-groups-grid">
        <BaseTemplate fitToWindow={true}>
          <BaseTemplate.Header>
            <HStack>
              <PersistentGridResetButton />
              <PersistentGridClearFiltersButton />
              <Button onClick={openAddRule}>Add Rule</Button>
              <Button variant="outline" onClick={openProcessModal}>Manage Processes</Button>
            </HStack>
          </BaseTemplate.Header>
          <BaseTemplate.Body>
            <Box w="100%" h="100%">
              <div className="ag-theme-quartz" style={{ width: "100%", height: "100%" }}>
                <PersistentGrid<FppRuleGroup>
                  rowData={ruleGroups}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  loading={loading}
                  animateRows
                  onRowClicked={handleRowClicked}
                  getRowStyle={() => ({ cursor: "pointer" })}
                />
              </div>
            </Box>
          </BaseTemplate.Body>
        </BaseTemplate>
      </PersistentGridProvider>

      {/* ------------------------------------------------------------------ */}
      {/* Rule Group Modal                                                     */}
      {/* ------------------------------------------------------------------ */}
      <Modal isOpen={isRuleOpen} onClose={handleRuleClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{selectedGroup ? "Edit Rule" : "Add Rule"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">

              <FormControl>
                <FormLabel>Process</FormLabel>
                <Select
                  value={ruleForm.process_id}
                  onChange={(e) => setRuleForm((f) => ({ ...f, process_id: Number(e.target.value) }))}
                >
                  {processes.map((p) => (
                    <option key={p.process_id} value={p.process_id}>{p.process_name}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input
                  placeholder="e.g. npower PDF invoices"
                  value={ruleForm.description}
                  onChange={(e) => setRuleForm((f) => ({ ...f, description: e.target.value }))}
                />
              </FormControl>

              <Divider />

              {/* Conditions list */}
              <Text fontWeight="semibold">
                Conditions <Text as="span" fontWeight="normal" fontSize="sm" color="gray.500">— all must match (AND)</Text>
              </Text>

              {conditions.length > 0 && (
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Field</Th>
                      <Th>Operator</Th>
                      <Th>Value</Th>
                      <Th>Case</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {conditions.map((c) =>
                      editingCondition?.condition_id === c.condition_id ? (
                        <Tr key={c.condition_id} bg="blue.50">
                          <Td>
                            <Select
                              size="sm"
                              value={editingCondition.field_name}
                              onChange={(e) => setEditingCondition((ec) => ec ? { ...ec, field_name: e.target.value } : ec)}
                            >
                              {FIELD_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                            </Select>
                          </Td>
                          <Td>
                            <Select
                              size="sm"
                              value={editingCondition.operator}
                              onChange={(e) => setEditingCondition((ec) => ec ? { ...ec, operator: e.target.value } : ec)}
                            >
                              {OPERATOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </Select>
                          </Td>
                          <Td>
                            <Input
                              size="sm"
                              value={editingCondition.match_value}
                              onChange={(e) => setEditingCondition((ec) => ec ? { ...ec, match_value: e.target.value } : ec)}
                            />
                          </Td>
                          <Td>
                            <Checkbox
                              isChecked={editingCondition.case_sensitive}
                              onChange={(e) => setEditingCondition((ec) => ec ? { ...ec, case_sensitive: e.target.checked } : ec)}
                            />
                          </Td>
                          <Td>
                            <HStack>
                              <Button size="xs" colorScheme="blue" onClick={handleEditConditionSave}>Save</Button>
                              <Button size="xs" variant="ghost" onClick={() => setEditingCondition(null)}>Cancel</Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ) : (
                        <Tr key={c.condition_id}>
                          <Td>{c.field_name}</Td>
                          <Td>{OPERATOR_OPTIONS.find((o) => o.value === c.operator)?.label ?? c.operator}</Td>
                          <Td maxW="160px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{c.match_value}</Td>
                          <Td>{c.case_sensitive ? "Yes" : "No"}</Td>
                          <Td>
                            <HStack>
                              <IconButton
                                aria-label="Edit condition"
                                icon={<FaPencil />}
                                size="xs"
                                variant="ghost"
                                onClick={() => setEditingCondition(c)}
                              />
                              <IconButton
                                aria-label="Delete condition"
                                icon={<FaTrash />}
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => handleDeleteCondition(c.condition_id, c.rule_group_id)}
                              />
                            </HStack>
                          </Td>
                        </Tr>
                      )
                    )}
                  </Tbody>
                </Table>
              )}

              {/* Add condition form */}
              <Box p={3} borderWidth={1} borderRadius="md" borderColor="gray.200" bg="gray.50">
                <Text fontSize="sm" fontWeight="medium" mb={2}>Add Condition</Text>
                <HStack align="flex-end" wrap="wrap" spacing={2}>
                  <FormControl w="auto" minW="120px">
                    <FormLabel fontSize="xs">Field</FormLabel>
                    <Select
                      size="sm"
                      value={conditionForm.field_name}
                      onChange={(e) => setConditionForm((f) => ({ ...f, field_name: e.target.value }))}
                    >
                      {FIELD_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                    </Select>
                  </FormControl>
                  <FormControl w="auto" minW="130px">
                    <FormLabel fontSize="xs">Operator</FormLabel>
                    <Select
                      size="sm"
                      value={conditionForm.operator}
                      onChange={(e) => setConditionForm((f) => ({ ...f, operator: e.target.value }))}
                    >
                      {OPERATOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Select>
                  </FormControl>
                  <FormControl flex={1} minW="140px">
                    <FormLabel fontSize="xs">Value</FormLabel>
                    <Input
                      size="sm"
                      placeholder="e.g. @npower\.com$"
                      value={conditionForm.match_value}
                      onChange={(e) => setConditionForm((f) => ({ ...f, match_value: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl w="auto">
                    <FormLabel fontSize="xs">Case sensitive</FormLabel>
                    <Checkbox
                      isChecked={conditionForm.case_sensitive}
                      onChange={(e) => setConditionForm((f) => ({ ...f, case_sensitive: e.target.checked }))}
                    />
                  </FormControl>
                  <Button size="sm" onClick={handleAddCondition} isDisabled={!selectedGroup}>
                    Add
                  </Button>
                </HStack>
                {!selectedGroup && (
                  <Text fontSize="xs" color="gray.500" mt={1}>Save the rule first to add conditions.</Text>
                )}
              </Box>

            </VStack>
          </ModalBody>

          <ModalFooter>
            {selectedGroup && (
              <Button colorScheme="red" variant="ghost" mr="auto" onClick={handleRuleDelete}>
                Delete Rule
              </Button>
            )}
            <Button variant="ghost" mr={3} onClick={handleRuleClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleRuleSave} isLoading={savingRule}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ------------------------------------------------------------------ */}
      {/* Process Modal                                                        */}
      {/* ------------------------------------------------------------------ */}
      <Modal isOpen={isProcessOpen} onClose={onProcessClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Manage Processes</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">

              {/* Existing processes */}
              {processes.length > 0 && (
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Process Name</Th>
                      <Th>Description</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {processes.map((p) => (
                      <Tr key={p.process_id}>
                        <Td fontWeight="medium">{p.process_name}</Td>
                        <Td color="gray.600">{p.description ?? "—"}</Td>
                        <Td>
                          <HStack>
                            <IconButton
                              aria-label="Edit process"
                              icon={<FaPencil />}
                              size="xs"
                              variant="ghost"
                              onClick={() => selectProcessForEdit(p)}
                            />
                            <IconButton
                              aria-label="Delete process"
                              icon={<FaTrash />}
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => handleProcessDelete(p)}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}

              <Divider />

              {/* Add / edit process form */}
              <Text fontWeight="medium">{selectedProcess ? "Edit Process" : "Add Process"}</Text>
              <FormControl>
                <FormLabel>Process Name</FormLabel>
                <Input
                  placeholder="e.g. MFR__NPO_INV"
                  value={processForm.process_name}
                  onChange={(e) => setProcessForm((f) => ({ ...f, process_name: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input
                  placeholder="e.g. npower PDF invoices via email"
                  value={processForm.description}
                  onChange={(e) => setProcessForm((f) => ({ ...f, description: e.target.value }))}
                />
              </FormControl>
              <HStack>
                {selectedProcess && (
                  <Button variant="ghost" onClick={() => { setSelectedProcess(null); setProcessForm({ process_name: "", description: "" }); }}>
                    Cancel Edit
                  </Button>
                )}
                <Button colorScheme="blue" onClick={handleProcessSave}>
                  {selectedProcess ? "Update Process" : "Add Process"}
                </Button>
              </HStack>

            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onProcessClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </InternalUserRoute>
  );
}

export default ProcessMapping;
